// src/pages/WorkSession.js
// FIELD OPS V2 COMPLETE FIX
// FULL COPY / PASTE READY

// ✅ Site radius enforcement
// ✅ Blocks fake clock-ins
// ✅ Live route replay tracking
// ✅ Clock out fixed (no total_hours issue)
// ✅ Break tracking fixed
// ✅ Resume tracking after break
// ✅ Better status messages
// ✅ Auto refresh
// ✅ Clean UI
// ✅ Team testing ready

import { useEffect, useState, useRef } from "react";
import {
  haversineDistanceMeters,
  haversineDistanceMetersRounded,
} from "@fieldsync/shared/utils";
import { shiftAPI, locationAPI } from "../services/api";

import {
  Loader2,
  CheckCircle2,
  MapPin,
  AlertTriangle,
} from "lucide-react";

export default function WorkSession() {
  const [activeShift, setActiveShift] =
    useState(null);

  const [locations, setLocations] =
    useState([]);

  const [selectedLocation, setSelectedLocation] =
    useState("");

  const [openShift, setOpenShift] =
    useState(false);

  const [worked, setWorked] =
    useState(0);

  const [breakSec, setBreakSec] =
    useState(0);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [statusText, setStatusText] =
    useState("");

  const [gpsText, setGpsText] =
    useState("");

  const watchRef = useRef(null);

  /* ========================================= */
  /* LOAD */
  /* ========================================= */

  useEffect(() => {
    load();

    const t = setInterval(load, 15000);

    return () => clearInterval(t);
  }, []);

  async function load() {
    try {
      const [shift, locs] =
        await Promise.all([
          shiftAPI.getActive(),
          locationAPI.getLocations(),
        ]);

      setActiveShift(shift || null);

      setLocations(
        Array.isArray(locs) ? locs : []
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

/* ========================================= */
/* BATTERY SAVER GPS TRACKING FIXED */
/* Replace your ENTIRE GPS TRACKING section */
/* ========================================= */

const intervalRef = useRef(null);
const lastLatRef = useRef(null);
const lastLngRef = useRef(null);

useEffect(() => {
  if (!activeShift) {
    stopTracking();
    return;
  }

  if (activeShift.break_started_at) {
    stopTracking();
    return;
  }

  startTracking();

  return () => stopTracking();
}, [activeShift]);

function stopTracking() {
  if (
    watchRef.current !== null &&
    navigator.geolocation
  ) {
    navigator.geolocation.clearWatch(
      watchRef.current
    );
    watchRef.current = null;
  }

  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }
}

async function savePoint(pos) {
  const lat = pos.coords.latitude;
  const lng = pos.coords.longitude;

  if (
    lastLatRef.current !== null &&
    lastLngRef.current !== null
  ) {
    const moved = haversineDistanceMeters(
      lastLatRef.current,
      lastLngRef.current,
      lat,
      lng
    );

    if (moved < 20) {
      setGpsText(
        "Stationary mode (battery saver)"
      );
      return;
    }
  }

  lastLatRef.current = lat;
  lastLngRef.current = lng;

  try {
    await shiftAPI.updateLiveLocation(
      activeShift.id,
      lat,
      lng
    );

    setGpsText("Tracking route");
  } catch (err) {
    console.error(err);
  }
}

function startTracking() {
  if (!navigator.geolocation) return;
  if (!activeShift?.id) return;

  /* save first point instantly */
  navigator.geolocation.getCurrentPosition(
    savePoint,
    () => {},
    {
      enableHighAccuracy: true,
      timeout: 10000,
    }
  );

  /* low battery live tracking */
  watchRef.current =
    navigator.geolocation.watchPosition(
      savePoint,
      () => {},
      {
        enableHighAccuracy: false,
        maximumAge: 15000,
        timeout: 15000,
      }
    );

  /* backup save every 2 mins */
  intervalRef.current = setInterval(() => {
    navigator.geolocation.getCurrentPosition(
      savePoint,
      () => {},
      {
        enableHighAccuracy: false,
        timeout: 10000,
      }
    );
  }, 120000);
}
  /* ========================================= */
  /* TIMER */
  /* ========================================= */

  useEffect(() => {
    let timer;

    if (activeShift?.clock_in_time) {
      timer = setInterval(() => {
        const now = Date.now();

        const start = new Date(
          activeShift.clock_in_time
        ).getTime();

        const savedBreak =
          activeShift.total_break_seconds ||
          0;

        const liveBreak =
          activeShift.break_started_at
            ? Math.floor(
                (now -
                  new Date(
                    activeShift.break_started_at
                  ).getTime()) /
                  1000
              )
            : 0;

        const total =
          Math.floor(
            (now - start) / 1000
          ) -
          savedBreak -
          liveBreak;

        setWorked(
          total > 0 ? total : 0
        );

        setBreakSec(liveBreak);
      }, 1000);
    }

    return () =>
      clearInterval(timer);
  }, [activeShift]);

  /* ========================================= */
  /* ACTIONS */
  /* ========================================= */

async function clockInSite() {
  if (!selectedLocation) {
    alert("Select site");
    return;
  }

  const site = locations.find(
    (x) => String(x.id) === String(selectedLocation)
  );

  if (!site) {
    alert("Site not found");
    return;
  }

  setSaving(true);

  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      try {
        const userLat = pos.coords.latitude;
        const userLng = pos.coords.longitude;

        const siteLat = Number(site.latitude);
        const siteLng = Number(site.longitude);

        const distance = haversineDistanceMetersRounded(
          userLat,
          userLng,
          siteLat,
          siteLng
        );

        const allowed = Number(
          site.radius_meters ||
          site.radius ||
          250
        );

        if (distance > allowed) {
          setSaving(false);

          alert(
            `Outside geofence. You are ${distance}m away. Allowed ${allowed}m.`
          );

          return;
        }

        await shiftAPI.clockIn({
          location_id: selectedLocation,
          latitude: siteLat,
          longitude: siteLng,
          shift_type: "site",
          verified: true,
        });

        setStatusText("On Site");

        await load();
      } catch (err) {
        alert(err.message);
      } finally {
        setSaving(false);
      }
    },
    () => {
      alert("GPS required for site clock in");
      setSaving(false);
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
    }
  );
}

  async function clockInOpen() {
    setSaving(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await shiftAPI.clockIn({
            latitude:
              pos.coords.latitude,
            longitude:
              pos.coords.longitude,
            shift_type: "open",
          });

          setStatusText(
            "Travelling"
          );

          await load();
        } catch (err) {
          alert(err.message);
        } finally {
          setSaving(false);
        }
      },
      async () => {
        try {
          await shiftAPI.clockIn({
            shift_type: "open",
          });

          setStatusText(
            "Travelling"
          );

          await load();
        } catch (err) {
          alert(err.message);
        } finally {
          setSaving(false);
        }
      }
    );
  }

  async function clockOut() {
    setSaving(true);

    try {
      stopTracking();

      await shiftAPI.clockOut();

      setStatusText("");
      setGpsText("");

      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleBreak() {
    setSaving(true);

    try {
      if (
        activeShift.break_started_at
      ) {
        await shiftAPI.endBreak();
      } else {
        stopTracking();
        await shiftAPI.startBreak();
      }

      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  /* ========================================= */

  function format(sec) {
    const h = Math.floor(
      sec / 3600
    );

    const m = Math.floor(
      (sec % 3600) / 60
    );

    const s = sec % 60;

    return `${String(h).padStart(
      2,
      "0"
    )}:${String(m).padStart(
      2,
      "0"
    )}:${String(s).padStart(
      2,
      "0"
    )}`;
  }

  /* ========================================= */

  if (loading) {
    return (
      <div className="text-gray-400 flex gap-2">
        <Loader2
          size={16}
          className="animate-spin"
        />
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div>
        <h1 className="text-2xl font-semibold">
          Work Session
        </h1>

        <p className="text-sm text-gray-400">
          Field tracking + GPS verified
        </p>
      </div>

      {activeShift ? (
        <div className="rounded-3xl bg-[#020617] border border-white/10 p-8 text-center">

          <div className="w-20 h-20 mx-auto rounded-full bg-green-500/15 flex items-center justify-center text-green-400">
            <CheckCircle2 size={34} />
          </div>

          <p className="mt-4 text-green-400">
            Clocked In
          </p>

          <h2 className="text-5xl font-bold mt-3">
            {format(worked)}
          </h2>

          <p className="mt-4 text-sm text-indigo-300">
            {statusText ||
              "Working"}
          </p>

          <p className="mt-1 text-xs text-gray-400">
            {gpsText}
          </p>

          {activeShift.break_started_at && (
            <p className="mt-4 text-amber-400">
              Break {format(breakSec)}
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-3 mt-6">

            <button
              disabled={saving}
              onClick={toggleBreak}
              className="py-4 rounded-2xl bg-amber-500"
            >
              {activeShift.break_started_at
                ? "End Break"
                : "Start Break"}
            </button>

            <button
              disabled={saving}
              onClick={clockOut}
              className="py-4 rounded-2xl bg-red-600"
            >
              End Shift
            </button>

          </div>

        </div>
      ) : (
        <div className="rounded-3xl bg-[#020617] border border-white/10 p-8 space-y-5">

          <button
            onClick={() =>
              setOpenShift(false)
            }
            className={`w-full py-3 rounded-2xl ${
              !openShift
                ? "bg-indigo-600"
                : "bg-white/5"
            }`}
          >
            Site Shift
          </button>

          <button
            onClick={() =>
              setOpenShift(true)
            }
            className={`w-full py-3 rounded-2xl ${
              openShift
                ? "bg-indigo-600"
                : "bg-white/5"
            }`}
          >
            Open Shift
          </button>

          {!openShift ? (
            <>
              <select
                value={
                  selectedLocation
                }
                onChange={(e) =>
                  setSelectedLocation(
                    e.target.value
                  )
                }
                className="w-full px-4 py-4 rounded-2xl bg-white text-black"
              >
                <option value="">
                  Select Site
                </option>

                {locations.map(
                  (loc) => (
                    <option
                      key={loc.id}
                      value={loc.id}
                    >
                      {loc.name}
                    </option>
                  )
                )}
              </select>

              <button
                disabled={saving}
                onClick={
                  clockInSite
                }
                className="w-full py-4 rounded-2xl bg-green-600"
              >
                {saving
                  ? "Starting..."
                  : "Clock In At Site"}
              </button>
            </>
          ) : (
            <button
              disabled={saving}
              onClick={
                clockInOpen
              }
              className="w-full py-4 rounded-2xl bg-blue-600"
            >
              {saving
                ? "Starting..."
                : "Start Open Shift"}
            </button>
          )}

        </div>
      )}

    </div>
  );
}
